import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../lib/format";
import { promena as izracunaj, ton as tonZa } from "../../lib/promena";
import type { Promena } from "../../lib/promena";

/** Jedna mera u redu: tekuća vrednost, ista prošle godine, i kako se čita. */
export type StabloMera = {
  sada: number | null | undefined;
  /** Isti period prošle godine. Izostavljeno kad poređenja nema. */
  pre?: number | null;
  /** „kg", „kom"… Prazno za novac. */
  jedinica?: string;
  decimala?: number;
  /** Da li je veći broj bolji. Za dospelo potraživanje NIJE. */
  viseJeBolje?: boolean;
  /**
   * `false` kad odnos ne znači ništa — nepotpun period.
   *
   * Oba broja se pokazuju, smer se ne piše: septembar 2026 nosi 22 dana a poredi se sa punim
   * mesecom. Oznaka u rečenici i smer u boji su ista tvrdnja dva puta, a boja pobeđuje.
   */
  tumaci?: boolean;
  /**
   * Udeo ove mere u ukupnom, u procentima — i isti udeo prošle godine.
   *
   * <p>Postoji zbog zemalja, jedinog taba sa DVA udela na istom redu: traka nosi udeo u marži, a
   * udeo količine nema gde drugde. Original ih traži oba, za obe godine.
   */
  udeo?: number | null;
  udeoPre?: number | null;
  /**
   * Mera koja na OVOM nivou ne postoji — nije nula, nego je nema.
   *
   * <p>Naplata se knjiži na kontu kupca po komercijalisti; red kupca i red artikla je ne nose.
   * Bez ove razlike prazna ćelija se čita kao „naplaćeno nula", a to je tvrdnja koju podaci ne
   * daju.
   */
  vanNivoa?: boolean;
  /**
   * Nula se ISPISUJE kao nula, ne kao crta.
   *
   * <p>Crta znači „nema vrednosti", i za većinu mera je to tačnije od 0,00. Kofe starosti su
   * izuzetak: kofa koje nema i kofa od nule čitale bi se isto, a samo jedno od toga je istina.
   */
  nulaSePise?: boolean;
};

export type StabloRed = {
  kljuc: string;
  ime: string;
  /** Šifra artikla ili kratka napomena uz ime. */
  oznaka?: string;
  oznakaTon?: "tiho" | "paznja";
  /** Udeo u ukupnom, u procentima. Sme da bude negativan. */
  udeo?: number | null;
  /**
   * Isti udeo prošle godine, pored trake.
   *
   * <p>Stoji tu a ne pod merom jer bi inače ista brojka izašla dvaput — jednom uz traku, jednom
   * kao sitan red ispod iste mere.
   */
  udeoPre?: number | null;
  /** Mere redom kolona; prva vodi — krupnija je i nosi boju. */
  mere: StabloMera[];
  /**
   * Sitan red ispod prve mere, za ono što se NE sme pribrojiti: komadi uz kilograme.
   * Stoji u boji pažnje, da se ne pročita kao deo tonaže.
   */
  dodatak?: string;
  deca?: StabloRed[];
  /** Red ima šta da pokaže, ali deca se tek dovlače na klik. */
  mozeDaSeOtvori?: boolean;
  /**
   * Šta piše kad su deca dovučena a nema ih nijedno.
   *
   * <p>Bez ovoga otvoren prazan red izgleda isto kao red koji se još učitava — a to su dva
   * različita stanja i čitalac ih mora razlikovati.
   */
  prazno?: string;
};

/** „ime" za prvu kolonu, ili redni broj mere. `null` = redosled sa servera. */
export type StabloSort = "ime" | number | null;

type Props = {
  /** Naslov prve kolone, pa po jedan za svaku meru. */
  kolone: string[];
  /**
   * Ređanje na klik po zaglavlju. Bez ovoga zaglavlje nije dugme i ne reaguje.
   *
   * <p>Podrazumevano se NE ređa: redosled sa servera je redosled štampanog izveštaja, a čitalac
   * koji ga gleda godinama mora da nađe svoj red tamo gde ga očekuje.
   */
  sort?: StabloSort;
  opadajuce?: boolean;
  naSortiranje?: (kolona: StabloSort) => void;
  /** Naslov kolone udela; prazno znači da trake nema. */
  naslovUdela?: string;
  /**
   * Decimala u koloni udela. Podrazumevano jedna; zemlje traže dve, jer se njihov udeo poredi sa
   * štampanim izveštajem, gde na strani 7 (period januar–avgust) stoji 44,36 % i 22,16 %. Dve
   * decimale su zbog PODELE sa tim izveštajem, ne tvrdnja da portal te iznose daje na mesecu.
   */
  decimalaUdela?: number;
  redovi: StabloRed[];
  otvoreni: Set<string>;
  naOtvaranje: (kljuc: string) => void;
  /** Ključ reda čija se deca upravo dovlače. */
  ucitava?: string | null;
  zbir?: StabloRed;
  locale: string;
};

/**
 * Registar koji se spušta po nivoima, zajednički za sve tabove izveštaja.
 *
 * <p>Zatečena tabela je sva tri nivoa crtala isto — razlikovala ih je samo uvlaka i sitna
 * strelica — a pri otvaranju grupe usred tabele je niklo DRUGO zaglavlje. Uz to je svaka ćelija
 * nosila tekući broj, prošlogodišnji i čip u boji: dvanaest podataka po redu, svi iste težine,
 * i red visok sto tačaka.
 *
 * <p>Ovde nivo nosi veličinu i težinu, zaglavlje je jedno, a poređenje nosi TRAKA: udeo u
 * ukupnom se vidi pre nego što se pročita ijedna cifra. Prva mera vodi i jedina je krupna;
 * ostale stoje tiše. Prošla godina nije sklonjena nego spuštena u sitan red ispod broja.
 *
 * <p>Smer se ne piše uvek kao procenat — vidi `lib/promena`.
 */
export function StabloRegistar({
  kolone,
  naslovUdela,
  sort = null,
  opadajuce = true,
  naSortiranje,
  decimalaUdela = 1,
  redovi,
  otvoreni,
  naOtvaranje,
  ucitava,
  zbir,
  locale,
}: Props) {
  const { t } = useTranslation();
  const brojMera = Math.max(1, kolone.length - 1);
  // Kolona udela se širi sama kad red nosi i prošlogodišnji udeo: inače drugi broj pojede traku.
  const dvaUdela = redovi.some((r) => r.udeoPre != null && Number.isFinite(r.udeoPre));
  const stil = {
    ["--stablo-mera" as string]: String(brojMera),
    ["--stablo-traka" as string]: naslovUdela ? (dvaUdela ? "250px" : "198px") : "0px",
  } as React.CSSProperties;

  // `treegrid`, ne obična tabela: redovi se otvaraju i nose nivo, pa čitač ekrana treba da
  // izgovori i dubinu i stanje. Prelazak sa `<table>` na mrežu ne sme da oduzme semantiku.
  return (
    <div className="stablo" role="treegrid" aria-label={kolone[0]} style={stil}>
      <div className="stablo-glava" role="row">
        <Naslov naslov={kolone[0]} kolona="ime" sort={sort} opadajuce={opadajuce} naSortiranje={naSortiranje} />
        {/* Udeo je izveden iz prve mere, pa svoje ređanje nema: ređa se tom merom. */}
        {naslovUdela ? <div role="columnheader">{naslovUdela}</div> : <div role="columnheader" />}
        {kolone.slice(1).map((naslov, i) => (
          <Naslov
            key={naslov}
            naslov={naslov}
            kolona={i}
            desno
            sort={sort}
            opadajuce={opadajuce}
            naSortiranje={naSortiranje}
          />
        ))}
      </div>

      <div className="stablo-telo">
        {redovi.map((red) => (
          <Grana
            key={red.kljuc}
            red={red}
            nivo={1}
            otvoreni={otvoreni}
            naOtvaranje={naOtvaranje}
            ucitava={ucitava}
            imaUdeo={!!naslovUdela}
            decimalaUdela={decimalaUdela}
            locale={locale}
            t={t}
          />
        ))}
      </div>

      {zbir ? (
        <div className="stablo-red stablo-zbir" role="row" data-nivo="0">
          <div className="stablo-ime" role="gridcell">
            <span className="stablo-strelica" />
            <span className="naziv">{zbir.ime}</span>
          </div>
          {naslovUdela ? (
            <Udeo udeo={zbir.udeo} udeoPre={zbir.udeoPre} decimala={decimalaUdela} locale={locale} />
          ) : (
            <div role="gridcell" />
          )}
          {zbir.mere.map((mera, i) => (
            <Celija
              key={i}
              mera={mera}
              vodeca={i === 0}
              dodatak={i === 0 ? zbir.dodatak : undefined}
              decimalaUdela={decimalaUdela}
              locale={locale}
              t={t}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Naslov({
  naslov,
  kolona,
  desno,
  sort,
  opadajuce,
  naSortiranje,
}: {
  naslov: string;
  kolona: StabloSort;
  desno?: boolean;
  sort: StabloSort;
  opadajuce: boolean;
  naSortiranje?: (kolona: StabloSort) => void;
}) {
  const aktivna = sort === kolona;
  if (!naSortiranje) {
    return (
      <div className={desno ? "stablo-mera" : undefined} role="columnheader">
        {naslov}
      </div>
    );
  }
  return (
    <div
      className={desno ? "stablo-mera" : undefined}
      role="columnheader"
      aria-sort={aktivna ? (opadajuce ? "descending" : "ascending") : "none"}
    >
      <button type="button" className="stablo-sort" onClick={() => naSortiranje(kolona)}>
        {naslov}
        <span aria-hidden="true">{aktivna ? (opadajuce ? " ↓" : " ↑") : ""}</span>
      </button>
    </div>
  );
}

type GranaProps = {
  red: StabloRed;
  nivo: number;
  otvoreni: Set<string>;
  naOtvaranje: (kljuc: string) => void;
  ucitava?: string | null;
  imaUdeo: boolean;
  decimalaUdela: number;
  locale: string;
  t: ReturnType<typeof useTranslation>["t"];
};

function Grana({ red, nivo, otvoreni, naOtvaranje, ucitava, imaUdeo, decimalaUdela, locale, t }: GranaProps) {
  const otvoren = otvoreni.has(red.kljuc);
  const moze = (red.deca && red.deca.length > 0) || !!red.mozeDaSeOtvori;
  const dovlaci = ucitava === red.kljuc;

  const sadrzaj = (
    <>
      <div className="stablo-ime" role="gridcell">
        <span className="stablo-strelica" aria-hidden="true">
          {moze ? (dovlaci ? "·" : otvoren ? "▾" : "▸") : ""}
        </span>
        <span className="naziv">{red.ime}</span>
        {red.oznaka ? (
          <span className={`stablo-oznaka${red.oznakaTon === "paznja" ? " paznja" : ""}`}>{red.oznaka}</span>
        ) : null}
      </div>
      {imaUdeo ? (
        <Udeo udeo={red.udeo} udeoPre={red.udeoPre} decimala={decimalaUdela} locale={locale} />
      ) : (
        <div role="gridcell" />
      )}
      {red.mere.map((mera, i) => (
        <Celija
          key={i}
          mera={mera}
          vodeca={i === 0}
          dodatak={i === 0 ? red.dodatak : undefined}
          decimalaUdela={decimalaUdela}
          locale={locale}
          t={t}
        />
      ))}
    </>
  );

  return (
    <Fragment>
      {moze ? (
        <button
          type="button"
          className="stablo-red"
          role="row"
          data-nivo={nivo}
          aria-level={nivo}
          aria-expanded={otvoren}
          onClick={() => naOtvaranje(red.kljuc)}
        >
          {sadrzaj}
        </button>
      ) : (
        <div className="stablo-red" role="row" data-nivo={nivo} aria-level={nivo}>
          {sadrzaj}
        </div>
      )}

      {otvoren && dovlaci ? (
        <div className="stablo-red stablo-poruka" role="row" data-nivo={Math.min(3, nivo + 1)}>
          <div className="stablo-ime" role="gridcell">
            <span className="sk-spinner" aria-hidden="true" />
            <span className="naziv">{t("common.loading", "Učitavanje…")}</span>
          </div>
        </div>
      ) : null}

      {otvoren && !dovlaci && red.deca && red.deca.length === 0 && red.prazno ? (
        <div className="stablo-red stablo-poruka" role="row" data-nivo={Math.min(3, nivo + 1)}>
          <div className="stablo-ime" role="gridcell">
            <span className="stablo-strelica" />
            <span className="naziv">{red.prazno}</span>
          </div>
        </div>
      ) : null}

      {otvoren && red.deca
        ? red.deca.map((dete) => (
            <Grana
              key={dete.kljuc}
              red={dete}
              nivo={Math.min(3, nivo + 1)}
              otvoreni={otvoreni}
              naOtvaranje={naOtvaranje}
              ucitava={ucitava}
              imaUdeo={imaUdeo}
              decimalaUdela={decimalaUdela}
              locale={locale}
              t={t}
            />
          ))
        : null}
    </Fragment>
  );
}

function Udeo({
  udeo,
  udeoPre,
  decimala,
  locale,
}: {
  udeo?: number | null;
  udeoPre?: number | null;
  decimala: number;
  locale: string;
}) {
  if (udeo == null || !Number.isFinite(udeo)) return <div className="stablo-udeo" role="gridcell" />;
  const minus = udeo < 0;
  // Traka pokazuje VELIČINU udela; minus se čita po boji i po predznaku brojke pored nje,
  // jer grupa koja maržu odnosi doprinosi isto toliko koliko bi je donela.
  const sirina = Math.min(100, Math.abs(udeo));
  return (
    <div className="stablo-udeo" role="gridcell">
      <div className="stablo-traka">
        <i className={minus ? "minus" : undefined} style={{ width: `${sirina}%` }} />
      </div>
      <span className={`pct${minus ? " minus" : ""}`}>
        {formatNumber(udeo, locale, { minimumFractionDigits: decimala, maximumFractionDigits: decimala })} %
      </span>
      {udeoPre != null && Number.isFinite(udeoPre) ? (
        <span className="pct-pre">
          {formatNumber(udeoPre, locale, { minimumFractionDigits: decimala, maximumFractionDigits: decimala })} %
        </span>
      ) : null}
    </div>
  );
}

function Celija({
  mera,
  vodeca,
  dodatak,
  decimalaUdela,
  locale,
  t,
}: {
  mera: StabloMera;
  vodeca: boolean;
  dodatak?: string;
  decimalaUdela: number;
  locale: string;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  if (mera.vanNivoa) {
    return (
      <div className={`stablo-mera${vodeca ? " vodeca" : ""}`} role="gridcell">
        <span className="van-nivoa" title={t("report.notAtThisLevel", "ne postoji na ovom nivou")}>
          ·
        </span>
      </div>
    );
  }

  const dec = mera.decimala ?? 2;
  const prazno = mera.sada == null || !Number.isFinite(mera.sada);
  const nula = !prazno && mera.sada === 0 && !mera.nulaSePise;
  const minus = !prazno && (mera.sada as number) < 0;
  // Nepotpun period ne dobija smer. `pre` se NE sakriva: vrednost prošle godine je poznata i
  // ostaje, ali zaključak iz para se ne piše.
  const tumaci = mera.tumaci ?? true;
  const p = tumaci ? izracunaj(mera.sada, mera.pre) : null;
  const smer = p == null ? "" : tonZa(p, mera.viseJeBolje ?? true);

  return (
    <div className={`stablo-mera${vodeca ? " vodeca" : ""}`} role="gridcell">
      <div className={`glavna${minus ? " minus" : ""}`}>
        {prazno || nula ? "—" : formatNumber(mera.sada as number, locale, { minimumFractionDigits: dec, maximumFractionDigits: dec })}
        {!prazno && !nula && mera.jedinica ? <span className="jed"> {mera.jedinica}</span> : null}
      </div>
      <div className="pod">
        {p == null ? null : <span className={`smer ${smer}`}>{tekstPromene(p, locale, t)}</span>}
        {mera.pre != null && Number.isFinite(mera.pre) && mera.pre !== 0 ? (
          <> · {formatNumber(mera.pre, locale, { minimumFractionDigits: dec, maximumFractionDigits: dec })}</>
        ) : null}
      </div>
      {mera.udeo != null && Number.isFinite(mera.udeo) ? (
        <div className="pod">
          {t("report.col.share", "Udeo")}{" "}
          {formatNumber(mera.udeo, locale, {
            minimumFractionDigits: decimalaUdela,
            maximumFractionDigits: decimalaUdela,
          })}{" "}
          %
          {mera.udeoPre != null && Number.isFinite(mera.udeoPre) ? (
            <>
              {" · "}
              {formatNumber(mera.udeoPre, locale, {
                minimumFractionDigits: decimalaUdela,
                maximumFractionDigits: decimalaUdela,
              })}{" "}
              %
            </>
          ) : null}
        </div>
      ) : null}
      {dodatak ? <div className="pod paznja">{dodatak}</div> : null}
    </div>
  );
}

/**
 * Tekst smera — pravilo je u `lib/promena`, ovde je samo jezik.
 *
 * <p>Ekran piše promenu („▲ 6,0 %"), a štampani izveštaj piše indeks („1,06"). Razlika je
 * namerna i nije gubitak: IZVOZ i dalje nosi indeks, pa čitalac koji dve strane poredi ćeliju
 * po ćeliju ima gde to da uradi. Na ekranu indeks ne radi — odnos dva negativna RUC-a ispada
 * pozitivan broj iako je gubitak porastao, a 99.160 kg prema 460 kg daje „215,56" umesto „×216".
 */
function tekstPromene(p: Promena, locale: string, t: ReturnType<typeof useTranslation>["t"]): string {
  switch (p.vrsta) {
    case "nema":
      return "—";
    case "novo":
      return t("report.promena.novo", "novo");
    case "nestalo":
      return t("report.promena.nestalo", "nema prodaje");
    case "uGubitak":
      return t("report.promena.uGubitak", "u gubitak");
    case "izGubitka":
      return t("report.promena.izGubitka", "iz gubitka");
    case "veciGubitak":
      return t("report.promena.veciGubitak", "veći gubitak");
    case "manjiGubitak":
      return t("report.promena.manjiGubitak", "manji gubitak");
    case "puta":
      return `×${formatNumber(p.faktor, locale, {
        minimumFractionDigits: p.faktor >= 10 ? 0 : 2,
        maximumFractionDigits: p.faktor >= 10 ? 0 : 2,
      })}`;
    case "procenat": {
      const strelica = p.procenat > 0 ? "▲" : p.procenat < 0 ? "▼" : "=";
      return `${strelica} ${formatNumber(Math.abs(p.procenat), locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} %`;
    }
  }
}
